import { getUiLocale } from '@/i18n/locale';
import { t, useUiLocale } from '@/i18n/locale';
import { mergeHistory } from './companionHistory';
import {
  CompanionContext as Context,
  type CompanionSessionValue as Session,
} from './CompanionContext';
import { useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { CompanionAudioProvider } from './CompanionVoiceControls';
import { ApiError } from '@/lib/api';
import {
  companionApi,
  defaultBuddy,
  type BuddyApi,
  type BuddyChatInput,
  type BuddyMessage,
  type BuddyPageContext,
  type BuddySnapshot,
} from './companionApi';

// eslint-disable-next-line react-refresh/only-export-components -- shared companion contract and hooks
export function companionError(error: unknown) {
  if (error instanceof ApiError && error.code === 'AI_PRICE_CHANGED')
    return t('The conversation price changed. Review the updated Send price before trying again.');
  if (error instanceof ApiError && error.code === 'COMPANION_PRIVATE_DETAIL')
    return t(
      'Choose a favourite or a little goal. Keep passwords, contact details and addresses private.',
    );
  if (error instanceof ApiError && error.code === 'COMPANION_MEMORY_DUPLICATE')
    return t('That is already on your memory shelf.');
  if (error instanceof ApiError && error.code === 'COMPANION_MEMORY_DISABLED')
    return t('Saved memories are switched off. Ask a grown-up to change this.');
  if (
    error instanceof ApiError &&
    ['COMPANION_REVISION_CONFLICT', 'COMPANION_CONTEXT_CHANGED'].includes(error.code)
  )
    return t('These details changed on another device. Refresh before trying again.');
  if (error instanceof Error && error.name === 'AbortError')
    return t('Stopped. Your words are still here.');
  return t('Could not connect just now. Your words are still here; you can try again.');
}
// eslint-disable-next-line react-refresh/only-export-components -- shared companion contract and hooks
export function pageContextForPath(path: string): BuddyPageContext | null {
  if (!path.startsWith('/learn/') && path !== '/learn') return null;
  if (/\/companion(?:\/|$)/.test(path)) return { page: 'home' };
  if (/\/profile|\/account|\/handoff|\/login|\/temporary|\/class-code/.test(path)) return null;
  const options: Array<[RegExp, BuddyPageContext]> = [
    [/\/study/, { page: 'study', title: t('Study') }],
    [/\/projects\//, { page: 'project', title: 'My project' }],
    [/\/projects/, { page: 'works', title: 'My projects' }],
    [
      /\/workspace|\/playground|\/blocks|\/code|\/music|\/video/,
      { page: 'studio', title: 'Creative workspace' },
    ],
    [/\/missions/, { page: 'lesson', title: 'My lesson' }],
    [/\/create/, { page: 'create', title: t('Create') }],
  ];
  return (
    options.find(([pattern]) => pattern.test(path))?.[1] || {
      page: 'catalog',
      title: 'Airbotix Learn',
    }
  );
}
export function CompanionSession(props: Parameters<typeof ScopedCompanionSession>[0]) {
  useUiLocale();
  return <ScopedCompanionSession key={props.kidId + ':' + (props.audience ?? 'kid')} {...props} />;
}
function ScopedCompanionSession({
  kidId,
  client = companionApi,
  audience = 'kid',
  initialMessages = [],
  pageOverride,
  children,
}: {
  kidId: string;
  client?: BuddyApi;
  audience?: 'kid' | 'parent';
  initialMessages?: BuddyMessage[];
  pageOverride?: BuddyPageContext;
  children: ReactNode;
}) {
  useUiLocale();
  const cache = useQueryClient(),
    location = useLocation(),
    queryKey = ['companion', audience, kidId];
  const query = useQuery({
    queryKey,
    queryFn: () => client.read(kidId, audience === 'parent'),
    retry: false,
    staleTime: 30_000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });
  const [setupDeferred, setSetupDeferred] = useState(false);
  const [returnTo, setReturnTo] = useState('/learn');
  const [returnState, setReturnState] = useState<unknown>(null);
  useEffect(() => {
    setSetupDeferred(false);
    setReturnTo('/learn');
    setReturnState(null);
  }, [kidId]);
  useEffect(() => {
    if (location.pathname.startsWith('/learn/companion')) setSetupDeferred(true);
    else if (location.pathname.startsWith('/learn')) {
      setReturnTo(location.pathname + location.search + location.hash);
      setReturnState(() => location.state);
    }
  }, [location.pathname, location.search, location.hash, location.state, kidId]);
  const [helpOpen, setHelpOpen] = useState(false),
    [messages, setMessages] = useState<BuddyMessage[]>(initialMessages),
    [draft, setDraft] = useState(''),
    [busy, setBusy] = useState(false),
    [paused, setPaused] = useState(false),
    [error, setError] = useState(''),
    [registered, setRegistered] = useState<{ path: string; context: BuddyPageContext } | null>(
      null,
    );
  const oldestCursor = useRef<string | null>(null),
    latestCursor = useRef<string | null>(null);
  const [hasEarlier, setHasEarlier] = useState(false),
    [loadingEarlier, setLoadingEarlier] = useState(false),
    [earlierError, setEarlierError] = useState(''),
    [syncError, setSyncError] = useState('');
  const historyAbort = useRef<AbortController | null>(null),
    olderAbort = useRef<AbortController | null>(null),
    syncBusy = useRef(false);
  const [historyReady, setHistoryReady] = useState(!client.history || audience !== 'kid');
  const history = useQuery({
    queryKey: ['companion-history', kidId],
    queryFn: () => client.history!(kidId),
    enabled: audience === 'kid' && !!client.history && !!query.data,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    staleTime: 0,
  });
  useEffect(() => {
    if (historyReady || !history.data || history.isFetching) return;
    if (history.data.messages.length) setMessages(history.data.messages);
    oldestCursor.current = history.data.oldest_cursor ?? null;
    latestCursor.current = history.data.newest_cursor ?? null;
    setHasEarlier(history.data.has_earlier);
    setHistoryReady(true);
  }, [historyReady, history.data, history.isFetching]);
  const messageRef = useRef(messages);
  messageRef.current = messages;
  const busyRef = useRef(false),
    abort = useRef<AbortController | null>(null),
    turn = useRef(0),
    contextStart = useRef(0),
    revision = useRef<number | null>(null),
    pending = useRef<BuddyChatInput | null>(null);
  const stop = useCallback(() => {
    turn.current++;
    abort.current?.abort();
    abort.current = null;
    busyRef.current = false;
    setBusy(false);
  }, []);
  const invalidate = useCallback(() => {
    stop();
    pending.current = null;
    contextStart.current = messageRef.current.length;
  }, [stop]);
  const update = (next: BuddySnapshot, reset = true) => {
    if (reset) invalidate();
    revision.current = next.revision;
    cache.setQueryData(queryKey, next);
  };
  useEffect(() => {
    if (query.data) {
      if (revision.current !== null && revision.current !== query.data.revision) invalidate();
      revision.current = query.data.revision;
    }
  }, [query.data, invalidate]);
  useEffect(() => {
    stop();
    setError('');
  }, [location.pathname, stop]);
  useEffect(() => () => abort.current?.abort(), []);
  const syncHistory = useCallback(async () => {
    if (
      !client.history ||
      audience !== 'kid' ||
      !historyReady ||
      document.hidden ||
      syncBusy.current ||
      busyRef.current
    )
      return;
    syncBusy.current = true;
    const control = new AbortController();
    historyAbort.current = control;
    try {
      for (let batch = 0; batch < 4; batch++) {
        const result = await client.history(
          kidId,
          latestCursor.current ? { after: latestCursor.current } : undefined,
          control.signal,
        );
        if (control.signal.aborted) return;
        setMessages((old) =>
          result.cursor_reset ? result.messages : mergeHistory(old, result.messages),
        );
        if (!oldestCursor.current || result.cursor_reset) {
          oldestCursor.current = result.oldest_cursor ?? null;
          setHasEarlier(result.has_earlier);
        }
        latestCursor.current = result.newest_cursor ?? latestCursor.current;
        if (!result.has_later) break;
      }
      setSyncError('');
    } catch {
      if (!control.signal.aborted) setSyncError(t('Could not check for new messages.'));
    } finally {
      syncBusy.current = false;
    }
  }, [client, audience, historyReady, kidId]);
  useEffect(() => {
    if (!historyReady || !client.history || audience !== 'kid') return;
    const timer = window.setInterval(() => void syncHistory(), 15000);
    const visible = () => {
      if (!document.hidden) void syncHistory();
    };
    document.addEventListener('visibilitychange', visible);
    window.addEventListener('focus', visible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', visible);
      window.removeEventListener('focus', visible);
      historyAbort.current?.abort();
      olderAbort.current?.abort();
    };
  }, [syncHistory, historyReady, client, audience]);
  const loadEarlier = async () => {
    if (!client.history || !oldestCursor.current || loadingEarlier) return;
    setLoadingEarlier(true);
    setEarlierError('');
    const control = new AbortController();
    olderAbort.current = control;
    try {
      const result = await client.history(kidId, { before: oldestCursor.current }, control.signal);
      if (control.signal.aborted) return;
      setMessages((old) =>
        result.cursor_reset ? result.messages : mergeHistory(old, result.messages),
      );
      oldestCursor.current = result.oldest_cursor ?? oldestCursor.current;
      setHasEarlier(result.has_earlier);
      if (result.cursor_reset) latestCursor.current = result.newest_cursor ?? null;
    } catch {
      if (!control.signal.aborted) setEarlierError(t('Could not load earlier messages.'));
    } finally {
      if (olderAbort.current === control) setLoadingEarlier(false);
    }
  };
  const page =
    pageOverride ||
    (registered?.path === location.pathname
      ? registered.context
      : pageContextForPath(location.pathname));
  const priceQuery = useQuery({
    queryKey: ['companion-price', kidId],
    queryFn: () => client.price(kidId),
    enabled: audience === 'kid' && !!query.data && (page?.page === 'home' || helpOpen),
    retry: false,
    staleTime: 30_000,
  });
  const send = async (text: string, context: BuddyPageContext) => {
    if (
      priceQuery.isError ||
      !historyReady ||
      busyRef.current ||
      paused ||
      !text.trim() ||
      !query.data ||
      priceQuery.data === undefined
    )
      return;
    const body =
      pending.current?.text === text &&
      JSON.stringify(pending.current.pageContext) === JSON.stringify(context)
        ? pending.current
        : {
            request_id: crypto.randomUUID(),
            memory_revision: query.data.revision,
            text: text.trim().slice(0, 500),
            expected_stars: priceQuery.data.stars,
            language: getUiLocale(),
            history: messageRef.current
              .slice(contextStart.current)
              .slice(-8)
              .map((m) => ({ role: m.role, content: m.content.slice(0, 1500) })),
            pageContext: context,
          };
    pending.current = body;
    setDraft(text);
    busyRef.current = true;
    setBusy(true);
    setError('');
    const current = ++turn.current;
    abort.current = new AbortController();
    try {
      const result = await client.chat(kidId, body, abort.current.signal);
      if (turn.current !== current) return;
      if (!result.reply.trim()) throw new Error('Empty reply');
      if (result.snapshot) update(result.snapshot, false);
      setMessages((previous) => [
        ...previous,
        { id: body.request_id + ':user', role: 'user', content: text },
        {
          id: body.request_id + ':assistant',
          role: 'assistant',
          content: result.reply.slice(0, 3000),
          speech_token: result.speech_token,
        },
      ]);
      setDraft('');
      pending.current = null;
    } catch (caught) {
      if (turn.current !== current) return;
      setError(companionError(caught));
      if (
        caught instanceof ApiError &&
        ['COMPANION_CONTEXT_CHANGED', 'COMPANION_REVISION_CONFLICT'].includes(caught.code)
      ) {
        invalidate();
        await query.refetch();
      }
    } finally {
      if (turn.current === current) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  };
  const value: Session = {
    kidId,
    audience,
    client,
    snapshot: query.data || null,
    profile: query.data?.profile || defaultBuddy,
    loading: query.isPending || (!historyReady && !history.isError),
    loadError: query.isError || (!historyReady && history.isError),
    messages,
    draft,
    setDraft,
    busy,
    paused,
    togglePause: () => {
      stop();
      setPaused((v) => !v);
      setError('');
    },
    error,
    setError,
    price: priceQuery.isError ? null : (priceQuery.data?.stars ?? null),
    priceUnavailable: priceQuery.isError,
    hasEarlier,
    loadingEarlier,
    earlierError,
    syncError,
    loadEarlier,
    syncHistory,
    send,
    stop: () => {
      const wasBusy = busyRef.current;
      stop();
      if (wasBusy) setError(t('Stopped. Your words are still here.'));
    },
    refresh: async () => {
      await query.refetch();
      if (audience === 'kid') {
        await priceQuery.refetch();
        if (!historyReady && client.history) await history.refetch();
      }
    },
    update,
    setupDeferred,
    deferSetup: () => setSetupDeferred(true),
    returnTo,
    returnState,
    helpOpen,
    setHelpOpen,
    page,
    registerPage: (context) => {
      const path = location.pathname;
      setRegistered(context ? { path, context } : null);
      return () => setRegistered((previous) => (previous?.path === path ? null : previous));
    },
  };
  return (
    <Context.Provider value={value}>
      <CompanionAudioProvider>{children}</CompanionAudioProvider>
    </Context.Provider>
  );
}
// eslint-disable-next-line react-refresh/only-export-components -- shared companion contract and hooks
export function useCompanionPage(context: BuddyPageContext | null) {
  const session = useContext(Context),
    register = session?.registerPage;
  const serialized = JSON.stringify(context);
  useEffect(() => register?.(context), [serialized]); // eslint-disable-line react-hooks/exhaustive-deps
}
